import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../../services/api";

const RCA_CATEGORIES = [
  "procedure",
  "training",
  "communicatie",
  "materiaal",
  "installatie",
  "onderhoud",
  "menselijke_fout",
  "planning",
  "externe_factor",
  "onbekend",
  "overig",
];

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function KamReportDetailPage() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [report, setReport] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignTo, setAssignTo] = useState("");
  const [actionDescription, setActionDescription] = useState("");
  const [actionDueDate, setActionDueDate] = useState("");
  const [closingNote, setClosingNote] = useState("");
  const [reopenReason, setReopenReason] = useState("");
  const [rootCauseCategory, setRootCauseCategory] = useState("onbekend");
  const [rootCauseDescription, setRootCauseDescription] = useState("");
  const [contributingFactors, setContributingFactors] = useState("");
  const [lessonsLearned, setLessonsLearned] = useState("");
  const [preventiveMeasures, setPreventiveMeasures] = useState("");

  const canAssign = permissions.includes("kam.report.assign");
  const canManageActions = permissions.includes("kam.actions.manage");
  const canClose = permissions.includes("kam.report.close");
  const canReopen = permissions.includes("kam.report.reopen");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [detailRes, permissionRes, usersRes] = await Promise.all([
        api.get(`/kam/reports/${id}`),
        api.get("/rbac/me"),
        api.get("/users"),
      ]);
      const reportData = detailRes?.data || null;
      setReport(reportData);
      setPermissions(
        Array.isArray(permissionRes?.data?.permissions)
          ? permissionRes.data.permissions.map((item) => String(item || "").trim().toLowerCase())
          : []
      );
      setUsers(Array.isArray(usersRes?.data) ? usersRes.data : []);
      setAssignTo(String(reportData?.assignedTo?._id || reportData?.assignedTo || ""));
      setClosingNote(String(reportData?.closingNote || ""));
      setRootCauseCategory(String(reportData?.rootCauseCategory || "onbekend"));
      setRootCauseDescription(String(reportData?.rootCauseDescription || ""));
      setContributingFactors(
        Array.isArray(reportData?.contributingFactors) ? reportData.contributingFactors.join(", ") : ""
      );
      setLessonsLearned(String(reportData?.lessonsLearned || ""));
      setPreventiveMeasures(String(reportData?.preventiveMeasures || ""));
    } catch (err) {
      setError(err?.response?.data?.message || "KAM melding detail laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const openActions = useMemo(
    () =>
      (Array.isArray(report?.actions) ? report.actions : []).filter(
        (item) => !["done", "cancelled"].includes(String(item?.status || ""))
      ),
    [report]
  );

  async function saveAssign() {
    if (!canAssign) return;
    try {
      setSaving(true);
      setError("");
      setNotice("");
      await api.patch(`/kam/reports/${id}`, { assignedTo: assignTo || null });
      setNotice("Toewijzing opgeslagen.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Toewijzing opslaan mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function addAction(event) {
    event.preventDefault();
    if (!canManageActions) return;
    if (!actionDescription.trim()) {
      setError("Actiebeschrijving is verplicht.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      setNotice("");
      await api.post(`/kam/reports/${id}/actions`, {
        description: actionDescription.trim(),
        dueDate: actionDueDate || undefined,
      });
      setActionDescription("");
      setActionDueDate("");
      setNotice("Actie toegevoegd.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Actie toevoegen mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function setActionStatus(actionId, status) {
    if (!canManageActions) return;
    try {
      setSaving(true);
      setError("");
      setNotice("");
      await api.patch(`/kam/reports/${id}/actions/${actionId}`, { status });
      setNotice("Actie bijgewerkt.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Actie bijwerken mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function closeReport() {
    if (!canClose) return;
    try {
      setSaving(true);
      setError("");
      setNotice("");
      await api.post(`/kam/reports/${id}/close`, {
        closingNote: closingNote || undefined,
      });
      setNotice("Melding afgesloten.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Afsluiten mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function reopenReport() {
    if (!canReopen) return;
    if (!reopenReason.trim()) {
      setError("Heropen-reden is verplicht.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      setNotice("");
      await api.post(`/kam/reports/${id}/reopen`, {
        reopenReason: reopenReason.trim(),
      });
      setReopenReason("");
      setNotice("Melding heropend.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Heropenen mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function saveRca() {
    try {
      setSaving(true);
      setError("");
      setNotice("");
      const factors = contributingFactors
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
      await api.patch(`/kam/reports/${id}`, {
        rootCauseCategory,
        rootCauseDescription: rootCauseDescription || undefined,
        contributingFactors: factors,
        lessonsLearned: lessonsLearned || undefined,
        preventiveMeasures: preventiveMeasures || undefined,
      });
      setNotice("RCA bijgewerkt.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "RCA opslaan mislukt");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-gray-500">Laden...</p>;
  }

  if (!report) {
    return <p className="text-sm text-red-700">{error || "Melding niet gevonden"}</p>;
  }

  const status = String(report?.status || "");
  const isTerminal = status === "afgesloten" || status === "geannuleerd";

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{report.title || "KAM melding"}</h2>
            <p className="text-sm text-gray-500">
              Status: <strong>{status}</strong> • Severity: <strong>{report.severity || "-"}</strong>
            </p>
          </div>
          <Link to="/pl/kam/meldingen" className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Terug naar lijst
          </Link>
        </div>
      </section>

      {error ? <section className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</section> : null}
      {notice ? <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</section> : null}

      <section className="grid gap-4 lg:grid-cols-3">
        <article className="rounded-xl border bg-white p-4 shadow-sm lg:col-span-2">
          <h3 className="text-sm font-semibold text-gray-900">Meldinggegevens</h3>
          <div className="mt-2 grid gap-2 text-sm md:grid-cols-2">
            <Field label="Type" value={report.type} />
            <Field label="Aangemaakt" value={formatDateTime(report.createdAt)} />
            <Field label="Project" value={report?.projectId?.naam || report?.projectId?.title || "-"} />
            <Field label="Klant" value={report?.clientId?.naam || "-"} />
            <Field label="Werkbon" value={report?.workorderId?.werkbonNummer || "-"} />
            <Field label="Installatie" value={report?.installationId?.naam || "-"} />
            <Field label="Locatie" value={report?.locationText || "-"} />
            <Field label="Reporter" value={report?.reportedBy?.naam || report?.reportedBy?.email || "-"} />
          </div>
          <p className="mt-3 whitespace-pre-line rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
            {report.description || "-"}
          </p>
        </article>

        <article className="rounded-xl border bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900">Toewijzing</h3>
          <select
            value={assignTo}
            onChange={(event) => setAssignTo(event.target.value)}
            disabled={!canAssign || saving}
            className="mt-2 w-full rounded border border-gray-300 px-2 py-2 text-sm"
          >
            <option value="">Niet toegewezen</option>
            {users.map((user) => (
              <option key={user._id} value={user._id}>
                {user.naam || user.email || user._id}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={saveAssign}
            disabled={!canAssign || saving}
            className="mt-2 w-full rounded bg-blue-700 px-3 py-2 text-sm text-white hover:bg-blue-800 disabled:opacity-50"
          >
            Toewijzing opslaan
          </button>
        </article>
      </section>

      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Acties ({openActions.length} open)</h3>
        </div>

        <form onSubmit={addAction} className="mt-3 grid gap-2 md:grid-cols-[1fr_200px_120px]">
          <input
            value={actionDescription}
            onChange={(event) => setActionDescription(event.target.value)}
            disabled={!canManageActions || saving || isTerminal}
            placeholder="Actiebeschrijving"
            className="rounded border border-gray-300 px-2 py-2 text-sm"
          />
          <input
            type="date"
            value={actionDueDate}
            onChange={(event) => setActionDueDate(event.target.value)}
            disabled={!canManageActions || saving || isTerminal}
            className="rounded border border-gray-300 px-2 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={!canManageActions || saving || isTerminal}
            className="rounded bg-slate-800 px-3 py-2 text-sm text-white hover:bg-slate-900 disabled:opacity-50"
          >
            Actie toevoegen
          </button>
        </form>

        <div className="mt-3 space-y-2">
          {(Array.isArray(report.actions) ? report.actions : []).map((action) => (
            <article key={action._id} className="rounded border border-gray-200 p-3 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-gray-900">{action.description}</p>
                  <p className="text-xs text-gray-500">
                    Owner: {action?.owner?.naam || action?.owner?.email || "-"} • Due: {action?.dueDate ? formatDateTime(action.dueDate) : "-"}
                  </p>
                </div>
                <div className="flex gap-1">
                  {["open", "in_progress", "done", "cancelled"].map((value) => (
                    <button
                      key={value}
                      type="button"
                      disabled={!canManageActions || saving || isTerminal || String(action.status) === value}
                      onClick={() => setActionStatus(action._id, value)}
                      className={`rounded px-2 py-1 text-xs ${
                        String(action.status) === value
                          ? "bg-blue-700 text-white"
                          : "border border-gray-300 bg-white text-gray-700"
                      } disabled:opacity-50`}
                    >
                      {value}
                    </button>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <article className="rounded-xl border bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900">Afsluiten</h3>
          <textarea
            rows={3}
            value={closingNote}
            onChange={(event) => setClosingNote(event.target.value)}
            disabled={!canClose || saving || isTerminal}
            placeholder="Closing note"
            className="mt-2 w-full rounded border border-gray-300 p-2 text-sm"
          />
          <button
            type="button"
            onClick={closeReport}
            disabled={!canClose || saving || isTerminal}
            className="mt-2 rounded bg-emerald-700 px-3 py-2 text-sm text-white hover:bg-emerald-800 disabled:opacity-50"
          >
            Melding afsluiten
          </button>
        </article>

        <article className="rounded-xl border bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900">Heropenen</h3>
          <textarea
            rows={3}
            value={reopenReason}
            onChange={(event) => setReopenReason(event.target.value)}
            disabled={!canReopen || saving}
            placeholder="Reden heropenen"
            className="mt-2 w-full rounded border border-gray-300 p-2 text-sm"
          />
          <button
            type="button"
            onClick={reopenReport}
            disabled={!canReopen || saving}
            className="mt-2 rounded bg-amber-700 px-3 py-2 text-sm text-white hover:bg-amber-800 disabled:opacity-50"
          >
            Melding heropenen
          </button>
        </article>
      </section>

      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900">Audit / statusgeschiedenis</h3>
        <div className="mt-2 space-y-2">
          {(Array.isArray(report.auditTrail) ? report.auditTrail : []).map((item) => (
            <article key={item._id} className="rounded border border-gray-200 p-2 text-xs">
              <p className="font-medium text-gray-800">{item.actie}</p>
              <p className="text-gray-500">{formatDateTime(item.createdAt)}</p>
            </article>
          ))}
          {!Array.isArray(report.auditTrail) || report.auditTrail.length === 0 ? (
            <p className="text-sm text-gray-500">Geen auditregels.</p>
          ) : null}
        </div>
      </section>

      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900">RCA & leren van situaties</h3>
        <div className="mt-2 grid gap-2">
          <label className="text-xs font-medium text-gray-700">
            Root cause categorie
            <select
              value={rootCauseCategory}
              onChange={(event) => setRootCauseCategory(event.target.value)}
              disabled={saving || isTerminal}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
            >
              {RCA_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs font-medium text-gray-700">
            Oorzaak omschrijving
            <textarea
              rows={3}
              value={rootCauseDescription}
              onChange={(event) => setRootCauseDescription(event.target.value)}
              disabled={saving || isTerminal}
              className="mt-1 w-full rounded border border-gray-300 p-2 text-sm"
            />
          </label>

          <label className="text-xs font-medium text-gray-700">
            Contributing factors (komma gescheiden)
            <input
              value={contributingFactors}
              onChange={(event) => setContributingFactors(event.target.value)}
              disabled={saving || isTerminal}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
            />
          </label>

          <label className="text-xs font-medium text-gray-700">
            Lessons learned
            <textarea
              rows={3}
              value={lessonsLearned}
              onChange={(event) => setLessonsLearned(event.target.value)}
              disabled={saving || isTerminal}
              className="mt-1 w-full rounded border border-gray-300 p-2 text-sm"
            />
          </label>

          <label className="text-xs font-medium text-gray-700">
            Preventieve maatregelen
            <textarea
              rows={3}
              value={preventiveMeasures}
              onChange={(event) => setPreventiveMeasures(event.target.value)}
              disabled={saving || isTerminal}
              className="mt-1 w-full rounded border border-gray-300 p-2 text-sm"
            />
          </label>

          <div>
            <button
              type="button"
              onClick={saveRca}
              disabled={saving || isTerminal}
              className="rounded bg-slate-800 px-3 py-2 text-sm text-white hover:bg-slate-900 disabled:opacity-50"
            >
              RCA opslaan
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <p>
      <span className="font-medium text-gray-700">{label}:</span> {value || "-"}
    </p>
  );
}
